import { FilterOutlined } from "@ant-design/icons"
import { Badge, Button, Flex, Select } from "antd"
import type { BaseOptionType } from "antd/es/select"
import React from "react"
import { useTranslation } from "react-i18next"

import { mergeSelectedIntoOptions } from "../../../../utils/select/resolve-options.ts"
import { neutral, shadows } from "./dashboardTokens"

import type { GlobalFilterOptions } from "../../hooks/useGlobalFilterOptions"
import {
  FILTER_DIMENSIONS,
  countActiveFilters,
  createEmptyFilters,
  type FilterDimension
} from "../../redux/globalFilters"
import type { FilterLabels, GlobalFilters } from "../../redux/portfolio.types"

const DIMENSION_LABEL_KEYS: Record<FilterDimension, string> = {
  project: "portfolio.filters.project",
  pm: "portfolio.filters.pm",
  client: "portfolio.filters.client",
  project_type: "portfolio.filters.projectType",
  project_status: "portfolio.filters.status",
  state: "portfolio.filters.state"
}

// Options like State carry a code value ("NY") and a spelled-out label ("New
// York"); match the query against both so typing either narrows the list.
export const filterByLabelOrValue = (
  input: string,
  option?: BaseOptionType
): boolean => {
  const needle = input.trim().toLowerCase()
  if (!needle) return true
  const label = String(option?.label ?? "").toLowerCase()
  const value = String(option?.value ?? "").toLowerCase()
  return label.includes(needle) || value.includes(needle)
}

export const mergeFilterDimension = (
  filters: GlobalFilters,
  dimension: FilterDimension,
  value: string[] | undefined
): GlobalFilters => ({ ...filters, [dimension]: value ?? [] })

export { mergeSelectedIntoOptions }

// Capture the human labels of the currently-selected values while the options
// are loaded, so they can be persisted and reused when a later cold load can't
// resolve them. Falls back to any previously stored label, never the raw id.
export const captureFilterLabels = (
  previous: FilterLabels,
  dimension: FilterDimension,
  values: string[],
  options: BaseOptionType[] | undefined
): FilterLabels => {
  const optionLabels = new Map(
    (options ?? []).map((option) => [
      String(option.value),
      String(option.label ?? option.value)
    ])
  )
  const dimensionLabels: Record<string, string> = {}
  for (const value of values) {
    const label = optionLabels.get(value) ?? previous[dimension]?.[value]
    if (label !== undefined && label !== value) dimensionLabels[value] = label
  }
  const next: FilterLabels = { ...previous }
  if (Object.keys(dimensionLabels).length > 0) next[dimension] = dimensionLabels
  else delete next[dimension]
  return next
}

interface FilterBarProps {
  filters: GlobalFilters
  options: GlobalFilterOptions
  filterLabels?: FilterLabels
  dateControl?: React.ReactNode
  loading?: boolean
  onChange: (next: GlobalFilters, nextLabels: FilterLabels) => void
}

export const FilterBar = ({
  filters,
  options,
  filterLabels = {},
  dateControl,
  loading,
  onChange
}: FilterBarProps) => {
  const { t } = useTranslation()
  const activeCount = countActiveFilters(filters)

  const handleDimensionChange = (dimension: FilterDimension) => (value: string[]) => {
    onChange(
      mergeFilterDimension(filters, dimension, value),
      captureFilterLabels(filterLabels, dimension, value, options[dimension])
    )
  }

  return (
    <Flex
      data-testid="dashboard-filter-bar"
      wrap="wrap"
      align="center"
      gap="small"
      style={{
        padding: "10px 12px",
        border: `1px solid ${neutral.border}`,
        borderRadius: 12,
        background: neutral.surface,
        boxShadow: shadows.card
      }}
    >
      <Flex align="center" gap={6} style={{ color: neutral.muted, fontWeight: 600 }}>
        <Badge count={activeCount} size="small" offset={[2, -2]}>
          <FilterOutlined />
        </Badge>
        <span>{t("portfolio.filters.title", "Filters")}</span>
      </Flex>

      {dateControl}

      {FILTER_DIMENSIONS.map((dimension) => (
        <Select<string[]>
          key={dimension}
          mode="multiple"
          allowClear
          showSearch
          loading={loading}
          maxTagCount="responsive"
          filterOption={filterByLabelOrValue}
          placeholder={t(DIMENSION_LABEL_KEYS[dimension])}
          value={filters[dimension]}
          options={mergeSelectedIntoOptions(
            options[dimension],
            filters[dimension],
            filterLabels[dimension]
          )}
          onChange={handleDimensionChange(dimension)}
          style={{ minWidth: 124 }}
        />
      ))}

      <Button
        size="small"
        onClick={() => onChange(createEmptyFilters(), {})}
        disabled={activeCount === 0}
      >
        {t("portfolio.filters.clear", "Clear")}
      </Button>
    </Flex>
  )
}
